import React, { useEffect, useRef, useState } from 'react';
import Icon from '../home/Icon';

/**
 * Scroll-driven scenes: a tall block holds a sticky stage, and how far you have
 * scrolled through the block picks the phase. Phase drives `.is-on`, and every
 * animated element carries `.ra-fx`, so one media query can switch the whole
 * thing off and show the finished state on small screens.
 */
const useScrollPhase = (ref, phases) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const small = window.matchMedia && window.matchMedia('(max-width: 900px)').matches;
    if (reduced || small) { setPhase(phases - 1); return undefined; }

    let frame = 0;
    const measure = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const travel = rect.height - window.innerHeight;
      if (travel <= 0) { setPhase(phases - 1); return; }
      const progress = Math.min(1, Math.max(0, -rect.top / travel));
      // Hold the first phase briefly so the source document is read before it moves.
      setPhase(Math.min(phases - 1, Math.floor(progress * (phases + 0.35))));
    };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(measure); };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [ref, phases]);

  return phase;
};

const fx = (on, extra = '') => `ra-fx ${on ? 'is-on' : ''} ${extra}`.trim();

const Scene = ({ index, eyebrow, title, lead, phases, children }) => {
  const ref = useRef(null);
  const phase = useScrollPhase(ref, phases);
  return (
    <div className="ra-scene" ref={ref}>
      <div className="ra-scene-stage">
        <div className="ra-scene-inner">
          <header className="ra-scene-head">
            <span className="ra-scene-num">{String(index).padStart(2, '0')}</span>
            <div>
              <span className="ra-scene-eyebrow">{eyebrow}</span>
              <h3 className="ra-scene-title">{title}</h3>
              <p className="ra-scene-lead">{lead}</p>
            </div>
          </header>
          <div className="ra-scene-canvas">{children(phase)}</div>
          <div className="ra-scene-progress" aria-hidden="true">
            {Array.from({ length: phases }).map((_, i) => (
              <span key={i} className={i <= phase ? 'is-on' : ''} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── 01 · A paper bill becomes an expense ───────────────────────────────── */

const BILL_LINES = [
  ['Office stationery — assorted', '₹12,000'],
  ['CGST 9%', '₹1,080'],
  ['SGST 9%', '₹1,080'],
];

const READ_CHIPS = [
  ['Vendor matched · Ratna Stationers', 'contact-round'],
  ['GSTIN 24AABCR…1Z6 verified', 'badge-check'],
  ['Ledger · Printing & Stationery', 'landmark'],
  ['Input GST ₹2,160 · ITC eligible', 'receipt-indian-rupee'],
];

const VOUCHER_ROWS = [
  ['Party', 'Ratna Stationers'],
  ['Ledger', 'Printing & Stationery'],
  ['Cost centre', 'Head Office'],
  ['Input GST', '₹1,080 + ₹1,080'],
];

const SceneBill = (phase) => (
  <div className="ra-flow">
    {/* The paper */}
    <div className="ra-paper-wrap">
      <span className="ra-paper-tag">Photo from your phone</span>
      <div className={fx(true, 'ra-paper')}>
      <div className={`ra-paper-scan ${phase >= 1 ? 'is-on' : ''}`} aria-hidden="true" />
      <div className="ra-paper-head">
        <span className="ra-paper-vendor">RATNA STATIONERS</span>
        <span className="ra-paper-sub">Tax Invoice · RS-2291</span>
      </div>
      <div className="ra-paper-body">
        {BILL_LINES.map(([label, value]) => (
          <div className="ra-paper-row" key={label}>
            <span>{label}</span><span>{value}</span>
          </div>
        ))}
      </div>
        <div className="ra-paper-total"><span>Total</span><strong>₹14,160</strong></div>
      </div>
    </div>

    {/* What AI read */}
    <div className="ra-flow-mid">
      <div className={`ra-flow-pill ${phase >= 1 ? 'is-on' : ''}`}>
        <Icon name="sparkles" size={14} />{phase >= 2 ? 'Prepared' : 'Reading'}
      </div>
      <div className="ra-flow-chips">
        {READ_CHIPS.map(([text, icon], i) => (
          <span className={fx(phase >= 1, 'ra-flow-chip')} style={{ transitionDelay: `${i * 110}ms` }} key={text}>
            <Icon name={icon} size={12} />{text}
          </span>
        ))}
      </div>
      <div className={`ra-flow-arrow ${phase >= 2 ? 'is-on' : ''}`} aria-hidden="true"><span /><span /><span /></div>
    </div>

    {/* The voucher */}
    <div className={fx(phase >= 2, 'ra-voucher')}>
      <div className="ra-voucher-head">
        <span>Expense voucher · draft</span>
        <span className="ra-voucher-badge">Needs your approval</span>
      </div>
      <div className="ra-voucher-rows">
        {VOUCHER_ROWS.map(([label, value], i) => (
          <div className={fx(phase >= 2, 'ra-voucher-row')} style={{ transitionDelay: `${180 + i * 90}ms` }} key={label}>
            <span className="ra-voucher-label">{label}</span>
            <span className="ra-voucher-value">{value}</span>
          </div>
        ))}
      </div>
      <div className="ra-voucher-total"><span>Booked value</span><strong>₹14,160</strong></div>
      <p className="ra-voucher-note"><Icon name="info" size={12} />Approve and AI posts it. Nothing is saved before you do.</p>
    </div>
  </div>
);

/* ─── 02 · A bank statement becomes a reconciled ledger ──────────────────── */

const STATEMENT = [
  ['02 May', 'NEFT · PATEL TRADERS', '+₹1,15,640', 'INV-7731', true],
  ['04 May', 'UPI · RATNA STATIONERS', '−₹14,160', 'EXP-0912', true],
  ['06 May', 'CHQ 004412', '−₹48,000', 'PB-4417', true],
  ['09 May', 'NEFT · MEERA INDUSTRIES', '+₹1,22,400', 'INV-7744', true],
  ['11 May', 'BANK CHARGES', '−₹236', 'Bank Charges', true],
  ['14 May', 'NEFT · UNKNOWN REF 88120', '+₹31,500', 'Needs you', false],
];

const SceneBank = (phase) => (
  <div className="ra-recon">
    <div className="ra-recon-panel">
      <div className="ra-recon-head">
        <span><Icon name="landmark" size={14} /> HDFC · Current A/C ••4417</span>
        <span className="ra-recon-meta">May statement · 6 lines</span>
      </div>
      <div className="ra-recon-rows">
        {STATEMENT.map(([date, desc, amt, match, ok], i) => {
          const lit = phase >= 1 && i <= (phase >= 2 ? STATEMENT.length : Math.min(i, 2));
          return (
            <div className={`ra-recon-row ${lit ? 'is-lit' : ''} ${lit && !ok ? 'is-flag' : ''}`} key={desc}
              style={{ transitionDelay: `${i * 90}ms` }}>
              <span className="ra-recon-date">{date}</span>
              <span className="ra-recon-desc">{desc}</span>
              <span className={`ra-recon-amt ${amt.startsWith('+') ? 'is-in' : ''}`}>{amt}</span>
              <span className={fx(lit, `ra-recon-match ${ok ? '' : 'is-flag'}`)} style={{ transitionDelay: `${120 + i * 90}ms` }}>
                {ok ? '✓ ' : '! '}{match}
              </span>
            </div>
          );
        })}
      </div>
    </div>

    <div className={fx(phase >= 2, 'ra-recon-summary')}>
      <div className="ra-recon-stat"><strong>5 of 6</strong><span>matched to a document</span></div>
      <div className="ra-recon-stat"><strong>1</strong><span>needs a person</span></div>
      <div className="ra-recon-stat"><strong>₹236</strong><span>auto-coded to Bank Charges</span></div>
      <p className="ra-recon-note">
        <Icon name="info" size={12} />
        AI matched by amount, date window and party name. The unmatched receipt is waiting for you to
        say which invoice it belongs to.
      </p>
    </div>
  </div>
);

/* ─── 03 · History becomes next month's indent ───────────────────────────── */

const HISTORY = [52, 61, 48, 74, 66, 83, 71, 92];
const FORECAST = [88, 96, 79, 104];

const SceneForecast = (phase) => (
  <div className="ra-fc">
    <div className="ra-fc-chart">
      <div className="ra-fc-bars">
        {HISTORY.map((h, i) => (
          <div className={fx(phase >= 0, 'ra-fc-bar')} style={{ height: `${h}%`, transitionDelay: `${i * 55}ms` }} key={`h${i}`} />
        ))}
        {FORECAST.map((h, i) => (
          <div className={fx(phase >= 1, 'ra-fc-bar is-fc')} style={{ height: `${h}%`, transitionDelay: `${i * 90}ms` }} key={`f${i}`} />
        ))}
      </div>
      <div className={fx(phase >= 1, 'ra-fc-band')} aria-hidden="true" />
      <div className={fx(phase >= 1, 'ra-fc-line')} aria-hidden="true">
        <span>Free stock · 240 boxes</span>
      </div>
      <div className="ra-fc-axis"><span>Last 8 months</span><span>Next 4 weeks</span></div>
    </div>

    <div className={fx(phase >= 2, 'ra-fc-callout')}>
      <span className="ra-fc-kind"><Icon name="line-chart" size={13} />Forecast · 12mm TMT</span>
      <div className="ra-fc-grid">
        <div><strong>310</strong><span>boxes expected</span></div>
        <div><strong>70</strong><span>short by day 24</span></div>
        <div><strong>9 days</strong><span>vendor lead time</span></div>
      </div>
      <div className="ra-fc-action">
        <span>Confidence &plusmn;8%. AI suggests an indent for <strong>120 boxes</strong> by the 15th.</span>
        <span className="ra-fc-cta">Draft the indent →</span>
      </div>
    </div>
  </div>
);

/* ─── The reel ───────────────────────────────────────────────────────────── */

const AIScenes = () => (
  <div className="ra-scenes">
    <Scene
      index={1}
      phases={3}
      eyebrow="Paper in, books out"
      title="A bill on the desk becomes an expense in your ledger."
      lead="Photograph it. AI reads the vendor, verifies the GSTIN, picks the right ledger and cost centre, and works out the input credit — then hands you a voucher to approve."
    >
      {SceneBill}
    </Scene>

    <Scene
      index={2}
      phases={3}
      eyebrow="Reconciliation"
      title="A bank statement becomes a reconciled month."
      lead="AI matches every line it can to an invoice, bill or charge by amount, date and party — codes the routine ones itself, and shows you only what genuinely needs a human."
    >
      {SceneBank}
    </Scene>

    <Scene
      index={3}
      phases={3}
      eyebrow="Forecasting"
      title="Last year's sales become next month's purchase order."
      lead="AI reads your own history, projects what is coming, compares it with free stock and the vendor's lead time, and tells you the date by which the indent has to go out."
    >
      {SceneForecast}
    </Scene>
  </div>
);

export default AIScenes;
